import { Link } from 'react-router';
import { EmptyState } from '@/components/EmptyState';
import {
  FIGURE_COLUMNS,
  drillHref,
  failedGroupsOf,
  figureOf,
  type DrillCell,
  type DutyAnalyticsWindowParam,
  type GroupedFigures,
} from '@/lib/dutyAnalytics';
import { DA_TABLE_HEAD, DA_TABLE_SCROLL } from '@/lib/dutyAnalyticsLayout';
import { cn } from '@/lib/utils';
import { DUTY_ANALYTICS_COPY } from '../copy';
import { CountLink } from './CountLink';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C §2] THE SEVEN-COLUMN FIGURES TABLE — ONE IMPLEMENTATION FOR EVERY TABLE ON THE PAGE.
 *
 *  ⚠️ A PLAIN `<table>`, NOT AN ANTD `Table`. The scroller needs a LITERAL `max-h` class (rule one of
 *  `dutyAnalyticsLayout.ts`), and antd's `scroll.y` writes an inline height and splits the header
 *  into a second `<table>`; there is no paging, sorting or selection to buy with it; and antd's per-cell
 *  cost has been measured in this repository. A sticky `<thead>` inside an `overflow-auto` box is CSS.
 *
 *  ⚠️ ROWS ARE IN THE SERVER'S ORDER and are never re-sorted here. Every non-zero count is a link to
 *  the rows behind it and every zero is plain (`CountLink`). A row's NAME opens nothing: a figures table
 *  is not a list of records, and each count is the way in.
 *
 *  ⚠️ [amended P7C.1 §4] ONE EXCEPTION, OFFERED BY THE CALLER: the commissionerate's Divisions table gives a
 *  real division's row a `nameHref` — that division's own page. It goes somewhere no count goes, so no
 *  destination is reached two ways, and every count in the row still opens its rows exactly as before. The
 *  link's accessible name starts with the visible name (WCAG 2.5.3).
 *
 *  ⚠️ THE COLUMNS SPAN THREE SECTIONS, SO ONE CAN FAIL WITHOUT THE OTHERS. A failed group renders "—" in
 *  its own columns with one sentence above the table naming them; the rest of the row renders normally.
 *
 *  ⚠️ U2 — A `plain` FOOTER ROW CANNOT CARRY A TARGET, BY TYPE. The commissionerate's totals on a
 *  division's page are a deliberate, figures-only disclosure that no drill parameter reaches;
 *  `cell?: never` makes giving that row a link a compile error rather than a review comment, and a test
 *  holds the proof with `@ts-expect-error`.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type FiguresTableRow = {
  readonly key: string;
  readonly orgUnitId: string;
  readonly label: string;
  /** The row's second fact, in its own column so a row stays one 40-pixel line. */
  readonly secondary?: string;
  readonly figures: GroupedFigures;
  readonly cell: DrillCell;
  /** [P7C.1 §4] Where the row's NAME leads, when anywhere — a division's own page, and nothing else. */
  readonly nameHref?: string;
};

export type FooterRow =
  | {
      readonly kind: 'linked';
      readonly testId: string;
      readonly label: string;
      readonly figures: GroupedFigures;
      readonly cell: DrillCell;
    }
  | {
      readonly kind: 'plain';
      readonly testId: string;
      readonly label: string;
      readonly figures: GroupedFigures;
      readonly cell?: never;
    };

const CELL = 'h-10 whitespace-nowrap border-b border-border px-3';
const NUMBER = 'text-right tabular-nums';
const HEAD = 'h-10 whitespace-nowrap border-b border-border px-3 text-xs font-medium text-muted-foreground';

const FigureCells = ({
  figures,
  cell,
  rowName,
  range,
  pageDivision,
  asDivisionId,
}: {
  figures: GroupedFigures;
  cell: DrillCell | undefined;
  rowName: string;
  range: DutyAnalyticsWindowParam;
  pageDivision: string | undefined;
  asDivisionId: string | undefined;
}) => (
  <>
    {FIGURE_COLUMNS.map((column) => {
      const value = figureOf(figures, column);
      return (
        <td key={column.key} className={cn(CELL, NUMBER)}>
          {value === null ? (
            <span data-metric={column.metric} data-unavailable="true" className="text-muted-foreground">
              {DUTY_ANALYTICS_COPY.unavailable}
            </span>
          ) : (
            <CountLink
              value={value}
              metric={column.metric}
              href={cell === undefined ? undefined : drillHref(column.metric, cell, range, pageDivision, asDivisionId)}
              label={DUTY_ANALYTICS_COPY.countLabel(column.metric, value, rowName)}
            />
          )}
        </td>
      );
    })}
  </>
);

export const FiguresTable = ({
  testId,
  labelledBy,
  nameHeader,
  secondaryHeader,
  rows,
  footer = [],
  range,
  pageDivision,
  asDivisionId,
  empty,
}: {
  testId: string;
  labelledBy: string;
  nameHeader: string;
  secondaryHeader?: string;
  rows: readonly FiguresTableRow[];
  footer?: readonly FooterRow[];
  range: DutyAnalyticsWindowParam;
  /** The page's own `?division=`, carried into every drill link for the way back (`drillHref`). */
  pageDivision?: string;
  /** [P7C.1 §4] The division this page is read AS, from the commissionerate — sent with every drill. */
  asDivisionId?: string;
  empty: string;
}) => {
  if (rows.length === 0) return <EmptyState size="sm" title={empty} />;

  const failed = new Set(
    [
      ...rows.map((row) => row.figures),
      ...footer.filter((row) => row.kind === 'linked').map((row) => row.figures),
    ].flatMap(failedGroupsOf),
  );
  const unavailable = FIGURE_COLUMNS.filter((column) => failed.has(column.group)).map(
    (column) => DUTY_ANALYTICS_COPY.columns[column.metric],
  );

  return (
    <div className="flex flex-col gap-2">
      {unavailable.length > 0 && (
        <p className="text-sm text-muted-foreground" data-testid={`${testId}-unavailable`}>
          {DUTY_ANALYTICS_COPY.unavailableColumns(unavailable)}
        </p>
      )}
      <div className={DA_TABLE_SCROLL} data-testid={`${testId}-scroll`}>
        <table aria-labelledby={labelledBy} className="w-full border-separate border-spacing-0 text-sm">
          <thead className={DA_TABLE_HEAD}>
            <tr>
              <th scope="col" className={cn(HEAD, 'text-left')}>
                {nameHeader}
              </th>
              {secondaryHeader !== undefined && (
                <th scope="col" className={cn(HEAD, 'text-left')}>
                  {secondaryHeader}
                </th>
              )}
              {FIGURE_COLUMNS.map((column) => (
                <th key={column.key} scope="col" className={cn(HEAD, 'text-right')}>
                  {DUTY_ANALYTICS_COPY.columns[column.metric]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} data-testid={`${testId}-row`} data-org-unit-id={row.orgUnitId}>
                <th scope="row" className={cn(CELL, 'text-left font-normal')}>
                  {row.nameHref === undefined ? (
                    row.label
                  ) : (
                    <Link
                      to={row.nameHref}
                      data-testid={`${testId}-name-link`}
                      aria-label={DUTY_ANALYTICS_COPY.divisionPageLabel(row.label)}
                      className="text-link underline-offset-4 hover:underline"
                    >
                      {row.label}
                    </Link>
                  )}
                </th>
                {secondaryHeader !== undefined && (
                  <td className={cn(CELL, 'text-muted-foreground')}>{row.secondary ?? ''}</td>
                )}
                <FigureCells
                  figures={row.figures}
                  cell={row.cell}
                  rowName={row.label}
                  range={range}
                  pageDivision={pageDivision}
                  asDivisionId={asDivisionId}
                />
              </tr>
            ))}
          </tbody>
          {footer.length > 0 && (
            <tfoot>
              {footer.map((row) => (
                <tr key={row.testId} data-testid={row.testId} data-footer={row.kind}>
                  <th scope="row" className={cn(CELL, 'text-left font-medium')}>
                    {row.label}
                  </th>
                  {secondaryHeader !== undefined && <td className={CELL} />}
                  <FigureCells
                    figures={row.figures}
                    cell={row.kind === 'linked' ? row.cell : undefined}
                    rowName={row.label}
                    range={range}
                    pageDivision={pageDivision}
                    asDivisionId={asDivisionId}
                  />
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};
